import { FC, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Button,
  Icon,
  IconButton,
} from '@kontentino/ui';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from 'app/components/drawer/Drawer';
import AllSelectionToggler from 'app/components/AllSelectionToggler';
import { useTrashPosts } from '../hooks/useTrashPosts';
import { useTrashMutations } from '../hooks/useTrashMutations';
import TrashPostItem from './TrashPostItem';
import TrashEmptyState from './TrashEmptyState';
import Popup from 'utils/popup';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  profileIds?: number[];
};

const TrashPanel: FC<Props> = ({ isOpen, onClose, profileIds }) => {
  const { t } = useTranslation();
  const [selectedPostIds, setSelectedPostIds] = useState<number[]>([]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedPostIds([]);
    }
  }, [isOpen]);

  const queryParams = { profileIds };

  const { posts, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } =
    useTrashPosts({
      ...queryParams,
      enabled: isOpen,
    });

  const { restoreSelectedFromTrash, permanentDeleteSelectedFromTrash } =
    useTrashMutations();

  const togglePostSelection = (postId: number) => {
    setSelectedPostIds((prev) =>
      prev.includes(postId)
        ? prev.filter((id) => id !== postId)
        : [...prev, postId],
    );
  };

  const selectAll = () => {
    setSelectedPostIds(posts.map((p) => p.id));
  };

  const selectNone = () => {
    setSelectedPostIds([]);
  };

  const clearSelection = () => {
    setSelectedPostIds([]);
  };

  const handleRestoreSelected = async () => {
    const count = selectedPostIds.length;
    const { isConfirmed } = await Popup.confirm({
      title: t('restoreSelectedPosts', {
        count,
        defaultValue_one: 'Restore selected post',
        defaultValue_other: 'Restore selected posts',
      }),
      text: t('restoreSelectedPostsConfirmation', {
        count,
        defaultValue_one:
          'Are you sure you want to restore {{count}} selected post from trash?',
        defaultValue_other:
          'Are you sure you want to restore {{count}} selected posts from trash?',
      }),
      confirmButtonText: t('restoreSelected', 'Restore selected'),
    });
    if (!isConfirmed) return;

    restoreSelectedFromTrash.mutate(selectedPostIds, {
      onSuccess: clearSelection,
    });
  };

  const handleDeleteSelected = async () => {
    const count = selectedPostIds.length;
    const { isConfirmed } = await Popup.confirm({
      title: t('deleteSelectedPosts', {
        count,
        defaultValue_one: 'Delete selected post permanently',
        defaultValue_other: 'Delete selected posts permanently',
      }),
      text: t('deleteSelectedPostsConfirmation', {
        count,
        defaultValue_one:
          'Are you sure you want to permanently delete {{count}} selected post? This action cannot be undone.',
        defaultValue_other:
          'Are you sure you want to permanently delete {{count}} selected posts? This action cannot be undone.',
      }),
      confirmButtonText: t('deleteSelected', 'Delete selected'),
    });
    if (!isConfirmed) return;

    permanentDeleteSelectedFromTrash.mutate(selectedPostIds, {
      onSuccess: clearSelection,
    });
  };

  const isEmpty = !isLoading && posts.length === 0;
  const hasSelection = selectedPostIds.length > 0;
  const allSelected =
    posts.length > 0 && selectedPostIds.length === posts.length;

  return (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DrawerContent overlayDataCy="trash-panel-backdrop" data-cy="trash-panel">
        <DrawerHeader className="tw-pb-2">
          <DrawerTitle>{t('trash', 'Trash')}</DrawerTitle>
          <DrawerClose asChild>
            <IconButton
              variant="ghost"
              size="small"
              icon={<Icon icon={faXmark} />}
              data-cy="trash-panel_close"
            />
          </DrawerClose>
        </DrawerHeader>

        {!isEmpty && (
          <div className="tw-px-4 tw-pb-2 tw-pt-1">
            <Alert variant="warning">
              {t(
                'trashRetentionInfo',
                'Posts in trash are only visible for 60 days.',
              )}
            </Alert>
          </div>
        )}

        {!isEmpty && posts.length > 0 && (
          <div className="tw-flex tw-items-center tw-justify-end tw-border-b tw-border-b-grayscale-30 tw-px-4 tw-py-2">
            <AllSelectionToggler
              selected={allSelected ? 'all' : hasSelection ? undefined : 'none'}
              onSelect={(value) =>
                value === 'all' ? selectAll() : selectNone()
              }
              className="tw-p-0 tw-text-sm"
              dataCy={{
                wrapper: 'trash-panel_all-none',
                allButton: 'trash-panel_select-all',
                noneButton: 'trash-panel_select-none',
              }}
            />
          </div>
        )}

        <div className="tw-flex-1 tw-overflow-y-auto">
          {isLoading && posts.length === 0 ? (
            <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
              <ActivityIndicator />
            </div>
          ) : isEmpty ? (
            <TrashEmptyState />
          ) : (
            <div className="tw-pt-2">
              {posts.map((post) => (
                <TrashPostItem
                  key={post.id}
                  post={post}
                  isSelected={selectedPostIds.includes(post.id)}
                  onToggleSelection={() => togglePostSelection(post.id)}
                />
              ))}
              {hasNextPage && (
                <div className="tw-p-4">
                  <Button
                    variant="tertiary"
                    className="tw-w-full"
                    onClick={() => fetchNextPage()}
                    isLoading={isFetchingNextPage}
                    data-cy="trash-panel_load-more"
                  >
                    {t('loadMore', 'Load more')}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        <DrawerFooter>
          <Button
            variant="tertiary"
            onClick={handleRestoreSelected}
            disabled={
              isEmpty ||
              !hasSelection ||
              restoreSelectedFromTrash.isLoading ||
              permanentDeleteSelectedFromTrash.isLoading
            }
            data-cy="trash-panel_restore-selected"
          >
            {t('restoreSelected', 'Restore selected')}
            {hasSelection && ` (${selectedPostIds.length})`}
          </Button>
          <Button
            variant="danger"
            onClick={handleDeleteSelected}
            disabled={
              isEmpty ||
              !hasSelection ||
              restoreSelectedFromTrash.isLoading ||
              permanentDeleteSelectedFromTrash.isLoading
            }
            data-cy="trash-panel_delete-selected"
          >
            {t('deleteSelected', 'Delete selected')}
            {hasSelection && ` (${selectedPostIds.length})`}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export default TrashPanel;
